import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";

import type {
  LineageDsmArtifactVersionRef,
  LineageDsmBand,
  LineageDsmCell,
  LineageDsmMatrixKind,
  LineageDsmResult,
} from "../../api/lineageDsm";

import "./lineage-dsm.css";

type DsmPresentation = "matrix" | "table";
type MatrixCoordinate = { row: number; column: number };

export interface LineageDsmMatrixProps {
  result: LineageDsmResult | null;
  matrixKind: LineageDsmMatrixKind;
  isLoading: boolean;
  error: Error | null;
  navigationError?: string | null;
  onMatrixKind: (kind: LineageDsmMatrixKind) => void;
  onDrill: (token: string) => void;
  onRoot: () => void;
  onNextPage: (cursor: string) => void;
  onOpenFocus: (selection: LineageDsmArtifactVersionRef) => void;
  onOpenProof: (selection: LineageDsmArtifactVersionRef) => void;
  onOpenImpact: (selection: LineageDsmArtifactVersionRef) => void;
}

const integer = new Intl.NumberFormat();

function compareCodePoints(left: string, right: string): number {
  const leftPoints = [...left].map((character) => character.codePointAt(0)!);
  const rightPoints = [...right].map((character) => character.codePointAt(0)!);
  const length = Math.min(leftPoints.length, rightPoints.length);
  for (let index = 0; index < length; index += 1) {
    if (leftPoints[index] !== rightPoints[index]) return leftPoints[index]! < rightPoints[index]! ? -1 : 1;
  }
  return leftPoints.length - rightPoints.length;
}

function matrixSemantics(kind: LineageDsmMatrixKind): {
  heading: string;
  description: string;
  relation: string;
  zero: string;
} {
  return kind === "dependency" ? {
    heading: "Dependency structure matrix",
    description: "Distinct admitted input-binding → output-binding paths between exact ArtifactVersion candidates.",
    relation: "binding paths",
    zero: "No admitted binding path in this returned matrix",
  } : {
    heading: "Model-context matrix",
    description: "Explicit instance → generic Artifact relations. Context is not a dependency edge.",
    relation: "instance-of relations",
    zero: "No explicit instance-of relation in this returned matrix",
  };
}

function heatBand(cell: LineageDsmCell | undefined): string {
  if (!cell) return "is-zero";
  if (cell.heat >= 800) return "is-heat-5";
  if (cell.heat >= 600) return "is-heat-4";
  if (cell.heat >= 400) return "is-heat-3";
  if (cell.heat >= 200) return "is-heat-2";
  return "is-heat-1";
}

function bandTypeLabel(band: LineageDsmBand): string {
  if (band.entity_kind !== "cluster") return band.entity_kind === "artifact" ? "Artifact" : "ArtifactVersion";
  if (band.cluster_kind === "remainder") return "Remainder cluster";
  if (band.cluster_kind === "partition") return "Deterministic partition";
  return "Semantic cluster";
}

function cellLabel(
  source: LineageDsmBand,
  target: LineageDsmBand,
  cell: LineageDsmCell | undefined,
  relation: string,
  zero: string,
): string {
  return cell
    ? `${source.label} to ${target.label}: ${integer.format(cell.count)} ${relation}; heat ${cell.heat} of 1000`
    : `${source.label} to ${target.label}: ${zero}`;
}

function BandActionPanel({
  band,
  peer,
  onDrill,
  onOpenFocus,
  onOpenProof,
  onOpenImpact,
}: {
  band: LineageDsmBand;
  peer?: LineageDsmBand | null;
  onDrill: (token: string) => void;
  onOpenFocus: (selection: LineageDsmArtifactVersionRef) => void;
  onOpenProof: (selection: LineageDsmArtifactVersionRef) => void;
  onOpenImpact: (selection: LineageDsmArtifactVersionRef) => void;
}) {
  const candidates = [band, peer].filter((candidate, index, values): candidate is LineageDsmBand => (
    Boolean(candidate) && values.findIndex((entry) => entry?.id === candidate!.id) === index
  ));
  return (
    <aside className="lineage-dsm__band-actions" aria-label="Selected DSM band details">
      {candidates.map((candidate) => (
        <section key={candidate.id}>
          <div>
            <span>{bandTypeLabel(candidate)}</span>
            <strong>{candidate.label}</strong>
            <small>{integer.format(candidate.member_count)} {candidate.member_count === 1 ? "member" : "members"}</small>
          </div>
          {candidate.entity_kind === "cluster" && candidate.drill_token ? (
            <button type="button" onClick={() => onDrill(candidate.drill_token!)}>
              Drill one level to {candidate.drill_kind === "leaves" ? "exact leaves" : "child clusters"}
            </button>
          ) : candidate.selection_ref ? (
            <nav aria-label={`${candidate.label} lineage actions`}>
              <button type="button" onClick={() => onOpenFocus(candidate.selection_ref!)}>Focus</button>
              <button type="button" onClick={() => onOpenProof(candidate.selection_ref!)}>Proof</button>
              <button type="button" onClick={() => onOpenImpact(candidate.selection_ref!)}>Impact</button>
            </nav>
          ) : null}
          {candidate.entity_kind === "artifact" && candidate.entity_ref && candidate.selection_ref ? (
            <small className="lineage-dsm__navigation-note">
              Context leaf {candidate.entity_ref.id}; navigation uses exact eligible ArtifactVersion {candidate.selection_ref.id}.
            </small>
          ) : null}
        </section>
      ))}
    </aside>
  );
}

export function LineageDsmMatrix({
  result,
  matrixKind,
  isLoading,
  error,
  navigationError = null,
  onMatrixKind,
  onDrill,
  onRoot,
  onNextPage,
  onOpenFocus,
  onOpenProof,
  onOpenImpact,
}: LineageDsmMatrixProps) {
  const [presentation, setPresentation] = useState<DsmPresentation>("matrix");
  const [coordinate, setCoordinate] = useState<MatrixCoordinate>({ row: 0, column: 0 });
  const [selectedBandId, setSelectedBandId] = useState<string | null>(null);
  const [selectedPeerId, setSelectedPeerId] = useState<string | null>(null);
  const cellRefs = useRef(new Map<string, HTMLButtonElement>());
  const headingRef = useRef<HTMLHeadingElement>(null);
  const previousResolvedIdentityRef = useRef<string | null>(null);
  const resultWasUnavailableRef = useRef(false);
  const restoreFocusAfterNavigationRef = useRef(false);
  const semantics = matrixSemantics(matrixKind);
  const matrixIdentity = result
    ? `${result.matrix_kind}\u0000${result.snapshot_token}\u0000${result.level}\u0000${result.breadcrumbs.map((crumb) => crumb.band_id).join("\u0000")}\u0000${result.bands.map((band) => band.id).join("\u0000")}`
    : "pending";

  const cellByPair = useMemo(() => new Map(
    (result?.cells ?? []).map((cell) => [`${cell.source_band_id}\u0000${cell.target_band_id}`, cell]),
  ), [result?.cells]);
  const highCoupling = useMemo(() => [...(result?.cells ?? [])]
    .sort((left, right) => right.count - left.count
      || compareCodePoints(left.source_band_id, right.source_band_id)
      || compareCodePoints(left.target_band_id, right.target_band_id))
    .slice(0, 3), [result?.cells]);

  useEffect(() => {
    if (!result) {
      if (previousResolvedIdentityRef.current !== null) resultWasUnavailableRef.current = true;
      return;
    }
    const previousIdentity = previousResolvedIdentityRef.current;
    const wasUnavailable = resultWasUnavailableRef.current;
    resultWasUnavailableRef.current = false;
    if (previousIdentity === matrixIdentity && !wasUnavailable) return;
    previousResolvedIdentityRef.current = matrixIdentity;
    const focused = result.focus_band_id ? result.bands.findIndex((band) => band.id === result.focus_band_id) : -1;
    const initial = previousIdentity === matrixIdentity ? coordinate.row : Math.max(0, focused);
    if (previousIdentity !== matrixIdentity) {
      setCoordinate({ row: initial, column: initial });
      setSelectedBandId(focused >= 0 ? result.focus_band_id : null);
      setSelectedPeerId(null);
    }
    const focusWasLost = typeof document !== "undefined" && document.activeElement === document.body;
    const shouldRestoreFocus = restoreFocusAfterNavigationRef.current || focusWasLost;
    restoreFocusAfterNavigationRef.current = false;
    if (shouldRestoreFocus) {
      globalThis.requestAnimationFrame?.(() => {
        if (presentation === "matrix" && result.bands.length > 0) {
          const column = previousIdentity === matrixIdentity ? coordinate.column : initial;
          cellRefs.current.get(`${initial}:${column}`)?.focus();
        } else {
          headingRef.current?.focus();
        }
      });
    }
  }, [coordinate.column, coordinate.row, matrixIdentity, presentation, result]);

  const focusCell = (next: MatrixCoordinate): void => {
    setCoordinate(next);
    globalThis.requestAnimationFrame?.(() => cellRefs.current.get(`${next.row}:${next.column}`)?.focus());
  };

  const onGridKeyDown = (event: KeyboardEvent<HTMLButtonElement>, row: number, column: number): void => {
    const bandCount = result?.bands.length ?? 0;
    if (!bandCount) return;
    let next = { row, column };
    if (event.key === "ArrowUp") next = { row: Math.max(0, row - 1), column };
    else if (event.key === "ArrowDown") next = { row: Math.min(bandCount - 1, row + 1), column };
    else if (event.key === "ArrowLeft") next = { row, column: Math.max(0, column - 1) };
    else if (event.key === "ArrowRight") next = { row, column: Math.min(bandCount - 1, column + 1) };
    else if (event.key === "Home" && (event.ctrlKey || event.metaKey)) next = { row: 0, column: 0 };
    else if (event.key === "End" && (event.ctrlKey || event.metaKey)) next = { row: bandCount - 1, column: bandCount - 1 };
    else if (event.key === "Home") next = { row, column: 0 };
    else if (event.key === "End") next = { row, column: bandCount - 1 };
    else return;
    event.preventDefault();
    focusCell(next);
  };

  const selectPair = (source: LineageDsmBand, target: LineageDsmBand): void => {
    setSelectedBandId(source.id);
    setSelectedPeerId(target.id);
  };

  const navigate = (action: () => void): void => {
    restoreFocusAfterNavigationRef.current = true;
    action();
  };

  const bands = result?.bands ?? [];
  const selectedBand = bands.find((band) => band.id === selectedBandId) ?? null;
  const selectedPeer = bands.find((band) => band.id === selectedPeerId) ?? null;
  const represented = result?.level === 0
    ? bands.reduce((sum, band) => sum + band.member_count, 0)
    : null;

  return (
    <section className="lineage-dsm" data-testid="lineage-dsm" data-matrix-kind={matrixKind}>
      <header className="lineage-dsm__header">
        <div>
          <span>Bounded server projection</span>
          <h2 ref={headingRef} tabIndex={-1}>{semantics.heading}</h2>
          <p>{semantics.description}</p>
        </div>
        <div className="lineage-dsm__controls">
          <div role="radiogroup" aria-label="DSM evidence mode">
            <button type="button" role="radio" aria-checked={matrixKind === "dependency"} onClick={() => onMatrixKind("dependency")}>Dependencies</button>
            <button type="button" role="radio" aria-checked={matrixKind === "model_context"} onClick={() => onMatrixKind("model_context")}>Model context</button>
          </div>
          <div role="group" aria-label="DSM presentation">
            <button type="button" aria-pressed={presentation === "matrix"} onClick={() => setPresentation("matrix")}>Heat matrix</button>
            <button type="button" aria-pressed={presentation === "table"} onClick={() => setPresentation("table")}>Accessible table</button>
          </div>
        </div>
      </header>

      {result && !result.warnings.includes("temporal_history_incomplete") ? (
        <div className="lineage-dsm__scope" role="status">
          <span>Level <b>{result.level}</b> of 8</span>
          <span><b>{integer.format(bands.length)}</b> bands</span>
          <span><b>{integer.format(result.cells.length)}</b> non-zero cells</span>
          {represented !== null ? <span><b>{integer.format(represented)}</b> authorized candidates represented</span> : null}
          <span>{result.aggregation_boundary === "artifact_version_binding" ? "ArtifactVersion binding boundary" : "Artifact instance-relation boundary"}</span>
        </div>
      ) : null}

      {result && result.level > 0 ? (
        <nav className="lineage-dsm__breadcrumbs" aria-label="DSM hierarchy breadcrumbs">
          <button type="button" onClick={() => navigate(onRoot)}>All lineage</button>
          {result.breadcrumbs.map((crumb, index) => (
            <span key={`${crumb.level}:${crumb.band_id}`} aria-current={index === result.breadcrumbs.length - 1 ? "location" : undefined}>
              <i aria-hidden="true">/</i>{crumb.label}
            </span>
          ))}
          <small>Use browser Back to return one signed level.</small>
        </nav>
      ) : null}

      {navigationError ? <div className="lineage-dsm__state is-error" role="alert">{navigationError}</div> : null}
      {!navigationError && error ? <div className="lineage-dsm__state is-error" role="alert">DSM unavailable: {error.message}</div> : null}
      {!navigationError && !error && isLoading ? <div className="lineage-dsm__state" role="status">Loading one exact bounded matrix…</div> : null}
      {!navigationError && !error && result?.warnings.includes("temporal_history_incomplete") ? (
        <div className="lineage-dsm__state is-warning" role="alert">
          Recorded history is incomplete at this cut. The matrix is intentionally empty; partial coupling is not displayed.
        </div>
      ) : null}
      {!navigationError && !error && result && !result.warnings.includes("temporal_history_incomplete") && bands.length === 0 ? (
        <div className="lineage-dsm__state" role="status">No authorized candidates match this query and filter scope.</div>
      ) : null}

      {!navigationError && !error && result && bands.length > 0 ? (
        <>
          <div className="lineage-dsm__body">
            <div className="lineage-dsm__matrix-panel">
              {presentation === "matrix" ? (
                <div className="lineage-dsm__scroller" tabIndex={0} aria-label="Scrollable dependency structure matrix">
                  <div
                    className="lineage-dsm__grid"
                    role="grid"
                    aria-label={semantics.heading}
                    aria-rowcount={bands.length + 1}
                    aria-colcount={bands.length + 1}
                    style={{ "--dsm-band-count": bands.length } as CSSProperties}
                  >
                    <div className="lineage-dsm__grid-header" role="row">
                      <div className="lineage-dsm__corner" role="columnheader">source ↓ · target →</div>
                      {bands.map((band) => (
                        <button
                          type="button"
                          role="columnheader"
                          key={`column:${band.id}`}
                          className={`lineage-dsm__axis is-column is-${band.cluster_kind ?? band.entity_kind}${band.id === result.focus_band_id ? " is-focused" : ""}`}
                          onClick={() => { setSelectedBandId(band.id); setSelectedPeerId(null); }}
                          title={band.label}
                        ><span>{band.label}</span><small>{integer.format(band.member_count)}</small></button>
                      ))}
                    </div>
                    {bands.map((source, row) => (
                      <div className="lineage-dsm__grid-row" role="row" key={`row:${source.id}`}>
                        <button
                          type="button"
                          role="rowheader"
                          className={`lineage-dsm__axis is-row is-${source.cluster_kind ?? source.entity_kind}${source.id === result.focus_band_id ? " is-focused" : ""}`}
                          onClick={() => { setSelectedBandId(source.id); setSelectedPeerId(null); }}
                          title={source.label}
                        ><span>{source.label}</span><small>{integer.format(source.member_count)}</small></button>
                        {bands.map((target, column) => {
                          const cell = cellByPair.get(`${source.id}\u0000${target.id}`);
                          return (
                            <button
                              type="button"
                              role="gridcell"
                              key={`${source.id}:${target.id}`}
                              ref={(node) => {
                                const key = `${row}:${column}`;
                                if (node) cellRefs.current.set(key, node); else cellRefs.current.delete(key);
                              }}
                              tabIndex={coordinate.row === row && coordinate.column === column ? 0 : -1}
                              className={`lineage-dsm__cell ${heatBand(cell)}${coordinate.row === row && coordinate.column === column ? " is-active" : ""}`}
                              aria-label={cellLabel(source, target, cell, semantics.relation, semantics.zero)}
                              aria-selected={selectedBandId === source.id && selectedPeerId === target.id}
                              onFocus={() => setCoordinate({ row, column })}
                              onKeyDown={(event) => onGridKeyDown(event, row, column)}
                              onClick={() => selectPair(source, target)}
                            ><span aria-hidden={!cell}>{cell ? integer.format(cell.count) : "·"}</span></button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="lineage-dsm__scroller" tabIndex={0} aria-label="Scrollable accessible DSM table">
                  <table className="lineage-dsm__table">
                    <caption>{semantics.heading}. Rows are sources; columns are targets. Zero means {semantics.zero.toLocaleLowerCase()}.</caption>
                    <thead><tr><th scope="col">Source ↓ / target →</th>{bands.map((band) => <th scope="col" key={band.id}>{band.label}</th>)}</tr></thead>
                    <tbody>
                      {bands.map((source) => (
                        <tr key={source.id}>
                          <th scope="row"><button type="button" onClick={() => { setSelectedBandId(source.id); setSelectedPeerId(null); }}>{source.label}</button></th>
                          {bands.map((target) => {
                            const cell = cellByPair.get(`${source.id}\u0000${target.id}`);
                            return <td key={target.id} className={heatBand(cell)}><button type="button" onClick={() => selectPair(source, target)} aria-label={cellLabel(source, target, cell, semantics.relation, semantics.zero)}>{cell?.count ?? 0}</button></td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <footer className="lineage-dsm__legend" aria-label="DSM heat legend">
                <span>0</span><i className="is-heat-1" /><i className="is-heat-2" /><i className="is-heat-3" /><i className="is-heat-4" /><i className="is-heat-5" /><span>relative maximum</span>
                <small>Heat is exact ceiling normalization within this response; compare counts across pages, not shade.</small>
              </footer>
            </div>
            <aside className="lineage-dsm__high-coupling" aria-label="Highest coupling in returned matrix">
              <header><span>Returned scope</span><h3>Highest coupling</h3></header>
              {highCoupling.length ? highCoupling.map((cell) => {
                const source = bands.find((band) => band.id === cell.source_band_id)!;
                const target = bands.find((band) => band.id === cell.target_band_id)!;
                return (
                  <button type="button" key={`${cell.source_band_id}:${cell.target_band_id}`} onClick={() => {
                    const row = bands.indexOf(source);
                    const column = bands.indexOf(target);
                    setCoordinate({ row, column });
                    selectPair(source, target);
                  }}>
                    <span>{source.label} <i aria-hidden="true">→</i> {target.label}</span>
                    <strong>{integer.format(cell.count)}</strong>
                    <small>{semantics.relation}</small>
                  </button>
                );
              }) : <p>No non-zero relationships in this returned matrix.</p>}
            </aside>
          </div>
          {selectedBand ? (
            <BandActionPanel
              band={selectedBand}
              peer={selectedPeer}
              onDrill={(token) => navigate(() => onDrill(token))}
              onOpenFocus={onOpenFocus}
              onOpenProof={onOpenProof}
              onOpenImpact={onOpenImpact}
            />
          ) : null}
          {result.next_cursor ? (
            <footer className="lineage-dsm__pagination">
              <p>This exact leaf page is an induced submatrix. Cross-page edges are absent, never approximated.</p>
              <button type="button" onClick={() => navigate(() => onNextPage(result.next_cursor!))}>Next exact leaf page</button>
            </footer>
          ) : result.level > 0 && result.level_kind === "leaves" ? (
            <footer className="lineage-dsm__pagination"><p>Complete final leaf page for this signed drill path.</p></footer>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
